'use client';

import { MutableRefObject, useEffect, useRef, useState } from 'react';

import Accordion from '@mui/material/Accordion/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary/AccordionSummary';
import classNames from 'classnames';
import { HtmlElement } from 'html-validate';
import { isFunction } from 'lodash';

import { IconCross } from '@/blocks/components/Icons';
import { useFaqController } from '@/providers/FAQProvider';
import typography from '@/styles/typography.module.scss';
import { isFiniteNumber } from '@/utils/number';

import styles from './styles.module.scss';

export type FaqCardQuestion = { question: React.ReactNode; answer: React.ReactNode };
export type FaqCardProps = {
  index?: number;
  cardData: FaqCardQuestion;
};

export function FaqCard({ cardData, index }: FaqCardProps) {
  const [opened, setOpened] = useState(false);
  const { faqController } = useFaqController();
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (
      isFiniteNumber(index) &&
      isFunction(faqController.registerFaqItem) &&
      ref.current instanceof HTMLElement
    ) {
      faqController.registerFaqItem(index, () => setOpened(true), ref as MutableRefObject<HTMLDivElement>);
    }
  }, [faqController, index]);

  return (
    <Accordion
      expanded={opened}
      className={classNames(styles.card)}
      onClick={(e) => {
        const clickOnSummery = (e.target as HTMLElement).closest('.MuiAccordionSummary-root');
        if (!opened || clickOnSummery) {
          setOpened(!opened);
        }
      }}
      ref={ref}
    >
      <AccordionSummary
        aria-controls={`panel${index}`}
        id={`panel${index}-header`}
        expandIcon={<IconCross />}
      >
        <h4 className={classNames(typography.h4)}>{cardData.question}</h4>
      </AccordionSummary>
      <AccordionDetails id={`panel${index}`}>
        <div className={typography.bodyM}>{cardData.answer}</div>
      </AccordionDetails>
    </Accordion>
  );
}
